import React from 'react';

// Trading style options
const TRADING_STYLES = [
  {
    key: 'intraday',
    label: 'Intraday',
    description: 'Trades within a single session (M5-H1 timeframes)'
  },
  {
    key: 'swing',
    label: 'Swing',
    description: 'Multi-day positions (H4-D timeframes)'
  }
];

// Required capabilities per rule type (informational - not user-selectable)
const RULE_CAPABILITIES = {
  KeyLevelProximity: {
    description: 'Analyzes market structure to identify and validate key price levels',
    capabilities: [
      { name: 'Market Data', desc: 'Current price, recent candles, and OHLC' },
      { name: 'Key Levels', desc: 'Swing highs/lows, round numbers, session levels' },
      { name: 'Price Action', desc: 'Recent price movement and momentum' }
    ]
  },
  StopLossPlacement: {
    description: 'Validates that stop loss is placed beyond significant market structure',
    capabilities: [
      { name: 'Market Data', desc: 'Current price, ATR for context' },
      { name: 'Swing Points', desc: 'Recent swing highs and lows' },
      { name: 'Volatility', desc: 'ATR-based distance validation' }
    ]
  },
  // Default for unknown LLM rules
  default: {
    description: 'AI-powered trade validation',
    capabilities: [
      { name: 'Market Data', desc: 'Price and technical data' }
    ]
  }
};

const LlmRuleForm = ({ libraryRule, userRule, config, onConfigChange }) => {

  const handlePromptChange = (e) => {
    onConfigChange({ ...config, prompt: e.target.value });
  };

  const handleTradingStyleChange = (style) => {
    onConfigChange({ ...config, tradingStyle: style });
  };

  // Get rule-specific capabilities
  const ruleClass = userRule?.ruleClass || libraryRule?.ruleClass || 'default';
  const ruleInfo = RULE_CAPABILITIES[ruleClass] || RULE_CAPABILITIES.default;

  // Current trading style
  const currentTradingStyle = config.tradingStyle || 'swing';

  // Use userRule's agentPrompt as the current prompt
  const currentPrompt = userRule?.agentPrompt || libraryRule?.agentPrompt || '';
  const displayValue = config.prompt !== undefined ? config.prompt : currentPrompt;

  return (
    <div className="llm-rule-form">
      {/* Trading Style Selection */}
      <div className="form-field">
        <label className="form-label">
          Trading Style
          <span className="form-label-hint">Adjusts analysis for your timeframe</span>
        </label>
        <div className="trading-style-grid">
          {TRADING_STYLES.map(style => (
            <button
              key={style.key}
              type="button"
              className={`trading-style-btn ${currentTradingStyle === style.key ? 'active' : ''}`}
              onClick={() => handleTradingStyleChange(style.key)}
            >
              <span className="style-label">{style.label}</span>
              <span className="style-desc">{style.description}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Rule Capabilities (Informational) */}
      <div className="form-field">
        <label className="form-label">
          AI Capabilities
          <span className="form-label-hint">Data automatically fetched for analysis</span>
        </label>
        <div className="capabilities-list">
          {ruleInfo.capabilities.map((cap, idx) => (
            <div key={idx} className="capability-item">
              <span className="capability-name">{cap.name}</span>
              <span className="capability-desc">{cap.desc}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="form-field">
        <label className="form-label">
          Custom Prompt
          <span className="form-label-hint">Optional: Override the default rule behavior</span>
        </label>
        <textarea
          value={displayValue}
          onChange={handlePromptChange}
          placeholder="Leave empty to use default prompt, or customize here..."
          rows={6}
          className="form-textarea"
        />
        {!config.prompt && currentPrompt && (
          <div className="form-hint">
            Using default prompt. Edit above to customize.
          </div>
        )}
      </div>
    </div>
  );
};

export default LlmRuleForm;
